import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * SSC: the "Get free shipping with" add-on row in the cart drawer and on the cart page
 * (markup in snippets/ssc-free-shipping-bar.liquid, part 'addons').
 *
 * A "+" with `data-variant-id` adds that variant; choosing a size in the native select adds that one. Adding follows Fabric's product form: it dispatches a
 * CartLinesUpdateEvent and resolves it with the cart sections, so cart-items-component morphs the
 * drawer / cart page (bar, items, this row) in place.
 */
class SscCartAddons extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    this.addEventListener('click', this.#handleClick, { signal: this.#controller.signal });
    this.addEventListener('change', this.#handleChange, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (variantId) this.#add(button, variantId);
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !this.contains(select) || !select.value) return;
    this.#add(select, select.value);
  };

  /**
   * @param {HTMLButtonElement | HTMLSelectElement} button - the control that triggered the add
   * @param {string} variantId
   */
  async #add(button, variantId) {
    if (this.getAttribute('aria-busy') === 'true') return;
    this.setAttribute('aria-busy', 'true');
    button.disabled = true;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const routes = window.Theme?.routes;
      const response = await fetch(routes?.cart_add_url ?? '/cart/add.js', {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const data = await response.json();
      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      const cartResponse = await fetch(`${routes?.cart_url ?? '/cart'}.js`, { credentials: 'same-origin' });
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ssc-cart-addons',
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });
      // Normally the morph replaces this markup; if a node survives unchanged, unlock it.
      setTimeout(() => {
        button.disabled = false;
        this.removeAttribute('aria-busy');
      }, 1500);
    } catch (error) {
      deferred.reject(error);
      console.warn('[ssc-cart-addons]', error);
      // The row stays (no morph happened): let the shopper try again.
      button.disabled = false;
      if (button instanceof HTMLSelectElement) button.value = '';
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('ssc-cart-addons')) customElements.define('ssc-cart-addons', SscCartAddons);
